import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface VideoIntroLoaderProps {
  onComplete: () => void;
}

const VIDEO_INTRO_KEY = 'ph_video_intro_shown';

export const shouldShowVideoIntro = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Skip on mobile (INP + LCP + bandwidth): 6.7 MB video kills mid-range Android.
  if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches) return false;
  // Respect "prefers-reduced-motion" (accessibility + performance).
  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  // Skip on Save-Data or slow connections.
  const conn = (navigator as any).connection;
  if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return false;
  const shown = sessionStorage.getItem(VIDEO_INTRO_KEY);
  return !shown;
};

export const markVideoIntroShown = (): void => {
  sessionStorage.setItem(VIDEO_INTRO_KEY, 'true');
};

const VideoIntroLoader = ({ onComplete }: VideoIntroLoaderProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Hard cap the intro so it never blocks LCP for longer than 2.5s.
    // The underlying page is already rendered beneath the overlay; as soon
    // as we unmount, the real hero can be measured as LCP.
    const lcpGuard = window.setTimeout(() => {
      handleComplete();
    }, 2500);

    // Try playing with sound; browser may block it, so fallback to muted
    video.muted = false;
    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    }

    const handleTimeUpdate = () => {
      if (video.duration) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };

    const handleEnded = () => {
      handleComplete();
    };

    const handleError = () => {
      setVideoError(true);
      handleComplete();
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    return () => {
      window.clearTimeout(lcpGuard);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, []);

  const handleComplete = () => {
    setIsExiting(true);
    markVideoIntroShown();
    setTimeout(() => {
      onComplete();
    }, 800);
  };


  if (videoError) {
    // Fallback - just complete immediately
    return null;
  }

  return (
    <AnimatePresence>
      {!isExiting ? (
        <motion.div
          className="fixed inset-0 z-[300] bg-black flex items-center justify-center overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        >
          {/* Video container with responsive scaling */}
          <div className="absolute inset-0 flex items-center justify-center">
            <video
              ref={videoRef}
              className="w-full h-full object-contain sm:object-cover scale-[1.2] sm:scale-100"
              src="/videos/punto_hispano_entrada_web.mp4"
              playsInline
              preload="metadata"
            />
          </div>

          {/* Vignette overlay */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)'
            }}
          />

          {/* Film corner marks - responsive sizes */}
          <div className="absolute top-3 left-3 sm:top-6 sm:left-6 w-6 h-6 sm:w-10 sm:h-10 border-l-2 border-t-2 border-white/20" />
          <div className="absolute top-3 right-3 sm:top-6 sm:right-6 w-6 h-6 sm:w-10 sm:h-10 border-r-2 border-t-2 border-white/20" />
          <div className="absolute bottom-3 left-3 sm:bottom-6 sm:left-6 w-6 h-6 sm:w-10 sm:h-10 border-l-2 border-b-2 border-white/20" />
          <div className="absolute bottom-3 right-3 sm:bottom-6 sm:right-6 w-6 h-6 sm:w-10 sm:h-10 border-r-2 border-b-2 border-white/20" />

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-0.5 sm:h-1 bg-white/10">
            <motion.div
              className="h-full bg-gradient-to-r from-punto to-red-400"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>

        </motion.div>
      ) : (
        <motion.div
          className="fixed inset-0 z-[300] bg-black"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        />
      )}
    </AnimatePresence>
  );
};

export default VideoIntroLoader;
